import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import { SCHEMA_ORG_ID, SITE_URL } from "@/lib/entitySchema";

const CANONICAL = `${SITE_URL}/learn/glossary`;

type Term = { term: string; definition: string; related?: string[] };

const TERMS: Term[] = [
  { term: "AI Overviews", definition: "Google's AI-generated answer block at the top of search results that summarizes information from multiple sources. Optimizing for AI Overviews is part of GEO (Generative Engine Optimization)." },
  { term: "Anchor Text", definition: "The clickable, visible text of a hyperlink. Descriptive anchor text helps Google understand what the linked page is about." },
  { term: "Backlink", definition: "A link from another website pointing to yours. High-quality, topically relevant backlinks remain one of Google's strongest ranking signals." },
  { term: "Canonical URL", definition: "The preferred URL for a page when multiple URLs serve the same or similar content. Set with a rel=canonical link tag to prevent duplicate-content issues." },
  { term: "Citation (Local SEO)", definition: "Any online mention of your business name, address, and phone number (NAP) — directories, social profiles, review sites. Consistency across citations strengthens local rankings." },
  { term: "Click-Through Rate (CTR)", definition: "The percentage of people who click a search result, ad, or link out of the total who saw it. A higher organic CTR can signal relevance to Google." },
  { term: "Conversion Rate Optimization (CRO)", definition: "The practice of improving a website so a higher percentage of visitors take a desired action — call, form fill, purchase." },
  { term: "Core Web Vitals", definition: "Google's set of performance metrics measuring loading (LCP), interactivity (INP), and visual stability (CLS). Affects rankings, especially on mobile." },
  { term: "Crawl Budget", definition: "The number of pages Google's bot will crawl on your site within a given timeframe. Important for large sites; rarely a concern for small-business sites under 500 pages." },
  { term: "E-E-A-T", definition: "Experience, Expertise, Authoritativeness, and Trustworthiness — Google's framework for evaluating content quality, especially for YMYL (Your Money or Your Life) topics." },
  { term: "Featured Snippet", definition: "A boxed answer at the top of Google's search results, pulled from a webpage. Earning featured snippets often requires structured, direct answers near the top of your content." },
  { term: "GEO (Generative Engine Optimization)", definition: "Optimizing content to be cited and quoted by AI search engines like ChatGPT, Perplexity, Gemini, and Google AI Overviews. The modern complement to traditional SEO." },
  { term: "Google Business Profile (GBP)", definition: "The free Google listing that powers your appearance in Google Maps and the local 3-pack. Formerly called Google My Business." },
  { term: "Local 3-Pack / Map Pack", definition: "The block of three local business results that appears with a map on Google for local-intent searches. Highly clicked and tied to your Google Business Profile." },
  { term: "Long-Tail Keyword", definition: "A longer, more specific search phrase (e.g., 'emergency plumber Briargate Colorado Springs' vs. 'plumber'). Lower volume but higher intent and easier to rank for." },
  { term: "Meta Description", definition: "The HTML attribute that summarizes a page's content. Often shown beneath the page title in search results. Doesn't directly affect rankings but heavily influences CTR." },
  { term: "NAP Consistency", definition: "Keeping your business Name, Address, and Phone number identical across every online listing. Inconsistent NAP confuses Google and weakens local rankings." },
  { term: "Off-Page SEO", definition: "Ranking factors that happen outside your website — backlinks, citations, social signals, brand mentions, reviews." },
  { term: "On-Page SEO", definition: "Ranking factors you control directly on each page — title tags, headings, content, internal links, schema, page speed." },
  { term: "Organic Traffic", definition: "Visitors who reach your site through unpaid search results. Distinct from paid (PPC), direct, social, and referral traffic." },
  { term: "PPC (Pay-Per-Click)", definition: "Paid search advertising where you bid for placement and pay only when someone clicks. Google Ads is the largest PPC platform." },
  { term: "Schema Markup / Structured Data", definition: "Code added to your page (typically JSON-LD) that helps search engines understand the content — business info, reviews, FAQs, articles, products. Powers rich results." },
  { term: "SERP", definition: "Search Engine Results Page — the page Google (or any engine) returns for a query. Modern SERPs include organic results, ads, AI Overviews, Map Packs, featured snippets, and more." },
  { term: "Service-Area Page", definition: "A dedicated landing page targeting a specific city or neighborhood you serve. Critical for local-service businesses competing across multiple markets." },
  { term: "Sitemap (XML)", definition: "A file listing the URLs on your site that you want search engines to crawl and index. Submitted via Google Search Console." },
  { term: "Title Tag", definition: "The HTML element specifying a page's title. Shown in browser tabs, search results, and social shares. One of the strongest on-page ranking signals." },
  { term: "URL Slug", definition: "The portion of a URL that identifies a specific page (e.g., /case-studies/hvac-colorado-springs). Should be short, descriptive, and keyword-relevant." },
  { term: "YMYL", definition: "Your Money or Your Life — Google's term for topics that can affect a person's health, finances, or safety. These pages are held to a higher E-E-A-T standard." },
];

const DEFINED_TERM_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "DefinedTermSet",
      "@id": `${CANONICAL}#glossary`,
      url: CANONICAL,
      name: "Colorado Web Impressions Marketing & SEO Glossary",
      publisher: { "@id": SCHEMA_ORG_ID },
      hasDefinedTerm: TERMS.map((t) => ({
        "@type": "DefinedTerm",
        name: t.term,
        description: t.definition,
        inDefinedTermSet: { "@id": `${CANONICAL}#glossary` },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Learn", item: `${SITE_URL}/learn` },
        { "@type": "ListItem", position: 3, name: "Glossary", item: CANONICAL },
      ],
    },
  ],
};

export const Route = createFileRoute("/learn/glossary")({
  head: () => ({
    meta: [
      { title: "SEO & Marketing Glossary | Plain-English Terms" },
      { name: "description", content: "Plain-English definitions for SEO, web design, and digital marketing terms. Built for Colorado small-business owners by Colorado Web Impressions." },
      { property: "og:title", content: "SEO & Marketing Glossary | Plain-English Terms" },
      { property: "og:description", content: "Every term defined the way we'd explain it on the phone — no jargon for jargon's sake." },
      { property: "og:url", content: CANONICAL },
    
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "SEO & Marketing Glossary — Colorado Web Impressions" },
      { name: "twitter:description", content: "Plain-English definitions of the SEO, web design, and digital marketing terms Colorado small-business owners actually need to know." },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(DEFINED_TERM_LD) }],
  }),
  component: GlossaryPage,
});

function GlossaryPage() {
  const grouped = TERMS.reduce<Record<string, Term[]>>((acc, t) => {
    const letter = t.term[0].toUpperCase();
    (acc[letter] ||= []).push(t);
    return acc;
  }, {});
  const letters = Object.keys(grouped).sort();

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Plain-English glossary"
        title={<>Marketing terms, <span className="text-brand-gold">defined the way we'd say them on the phone.</span></>}
        description="Every SEO, web design, and digital marketing term you'll hear from us — written in language a busy owner can actually use."
      />

      <nav aria-label="Jump to letter" className="border-b border-border bg-muted/30">
        <div className="mx-auto max-w-5xl px-5 py-4 lg:px-8">
          <ul className="flex flex-wrap gap-1.5">
            {letters.map((l) => (
              <li key={l}>
                <a href={`#letter-${l}`} className="grid h-8 w-8 place-items-center rounded-md border border-border bg-card text-xs font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep">
                  {l}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          {letters.map((letter) => (
            <div key={letter} id={`letter-${letter}`} className="scroll-mt-24 pb-12">
              <h2 className="flex items-center gap-3 text-2xl font-extrabold text-foreground">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-gradient-gold text-brand-black shadow-gold">{letter}</span>
              </h2>
              <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                {grouped[letter].map((t) => (
                  <div key={t.term} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                    <dt className="flex items-start gap-2 text-base font-extrabold text-foreground">
                      <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep" />
                      {t.term}
                    </dt>
                    <dd className="mt-2 text-sm leading-relaxed text-foreground/85">{t.definition}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}

          <div className="mt-8 rounded-2xl border border-border bg-muted/40 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              Term we missed? <Link to="/contact" className="font-bold text-brand-gold-deep hover:underline">Tell us</Link> and we'll add it.
            </p>
          </div>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
